import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useFetch, { CachePolicies } from 'use-http';
import {
    Box,
    Grid,
    Paper,
    Typography,
    TextField,
    Button,
    LinearProgress,
    IconButton,
    Stack,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Link,
    Alert,
} from '@mui/material';
import { ArrowBack, ArrowForward, MenuBook, School, Home as HomeIcon, Flag, FlagOutlined } from '@mui/icons-material';
import {
    AnnotationBatch,
    Query,
    RubricQuestion,
    AnnotationResponse,
    Annotation,
} from '../api/Annotation';

export const Annotate = () => {
    const { batchId } = useParams<{ batchId: string }>();

    // State management
    const [currentIndex, setCurrentIndex] = useState(0);
    const [batch, setBatch] = useState<AnnotationBatch | null>(null);
    const [responses, setResponses] = useState<Record<string, string | string[]>>({});
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [currentFigureTab, setCurrentFigureTab] = useState(0); // For figures category tabs
    const [validationDialogOpen, setValidationDialogOpen] = useState(false);
    const [unansweredQueries, setUnansweredQueries] = useState<string[]>([]);
    const [inMemoryAnnotations, setInMemoryAnnotations] = useState<Record<string, Annotation>>({});
    
    // Flag state - tracks if query/response is flagged as hallucinated/unrelated
    const [isFlagged, setIsFlagged] = useState(false);
    const [flagRationale, setFlagRationale] = useState('');
    const [flagDialogOpen, setFlagDialogOpen] = useState(false);

    // API hooks
    const { get: getBatch, loading: batchLoading, error: batchError } = useFetch<AnnotationBatch>(
        `/api/batch/${batchId}`,
        { cachePolicy: CachePolicies.NO_CACHE }
    );

    // Load batch data
    useEffect(() => {
        if (batchId) {
            loadBatch();
        }
    }, [batchId]);

    const loadBatch = async () => {
        const data = await getBatch();
        if (data) {
            // Always start fresh - no pre-existing annotations
            data.annotations = [];
            setBatch(data);
        }
    };

    // When switching queries, save current to in-memory state
    const saveCurrentToMemory = () => {
        if (!batch) return;
        
        const currentQuery = batch.queries[currentIndex];
        if (!currentQuery) return;

        const annotation: Annotation = {
            queryId: currentQuery.id,
            responses: Object.entries(responses).map(([questionId, answer]) => ({
                questionId,
                answer,
            })),
            additionalNotes,
            timestamp: new Date().toISOString(),
            flagged: isFlagged,
            flagRationale: isFlagged ? flagRationale : undefined,
        };

        setInMemoryAnnotations(prev => ({
            ...prev,
            [currentQuery.id]: annotation
        }));
    };

    // Load responses from in-memory state
    const loadResponsesFromMemory = (queryId: string) => {
        const annotation = inMemoryAnnotations[queryId];
        if (annotation) {
            const responsesMap: Record<string, string | string[]> = {};
            annotation.responses.forEach((resp) => {
                responsesMap[resp.questionId] = resp.answer;
            });
            setResponses(responsesMap);
            setAdditionalNotes(annotation.additionalNotes || '');
            setIsFlagged(annotation.flagged || false);
            setFlagRationale(annotation.flagRationale || '');
        } else {
            setResponses({});
            setAdditionalNotes('');
            setIsFlagged(false);
            setFlagRationale('');
        }
        
        // Reset figure tab when changing queries
        setCurrentFigureTab(0);
    };

    // Get rubric questions for current query
    const getRubricQuestions = useCallback((): RubricQuestion[] => {
        if (!batch) return [];
        
        const currentQuery = batch.queries[currentIndex];
        if (!currentQuery) return [];
        
        // Generate rubric questions based on category and figures
        const questions: RubricQuestion[] = [];
        const figuresInfo = (currentQuery as any).figures_info || [];
        
        if (currentQuery.category === 'general') {
            // For each figure/table referenced, ask if it supports primary conclusions
            figuresInfo.forEach((fig: string, idx: number) => {
                questions.push({
                    id: `general_fig_${idx + 1}`,
                    questionText: `Does ${fig} directly support one of the paper's primary conclusions?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'No',
                            followUpQuestions: [
                                {
                                    id: `general_fig_${idx + 1}_q1a_appropriate`,
                                    questionText: `If No: Is the out-of-scope figure or table appropriate per the query?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                                {
                                    id: `general_fig_${idx + 1}_q1b_explicitly_out_of_scope`,
                                    questionText: `If No: Does the response explicitly detail that the out-of-scope figure or table is out-of-scope?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });
                questions.push({
                    id: `general_fig_${idx + 1}_q2_represents`,
                    questionText: `Does the response identify what ${fig} represents (e.g., “an infographic about X” or “a flow diagram representing Y”), rather than only citing it by number (e.g., “See ${fig}”)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                });
            });
        } else if (currentQuery.category === 'sections') {
            // For each figure/table, ask if it's scoped to the section queried about
            figuresInfo.forEach((fig: string, idx: number) => {
                questions.push({
                    id: `sections_fig_${idx + 1}`,
                    questionText: `Looking at ${fig}, is the referenced figure or table directly scoped to the sections or methods the query asked about?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'No',
                            followUpQuestions: [
                                {
                                    id: `sections_fig_${idx + 1}_q1a_appropriate`,
                                    questionText: `If No, is this out-of-scope figure or table still appropriate for the query?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                                {
                                    id: `sections_fig_${idx + 1}_q1b_explicitly_out_of_scope`,
                                    questionText: `If No, does the response explicitly state that this figure or table is out-of-scope?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });
                questions.push({
                    id: `sections_fig_${idx + 1}_q2_represents`,
                    questionText: `Does the response identify what ${fig} represents (e.g., “an infographic about X” or “a flow diagram representing Y”), rather than only citing it by number (e.g., “See ${fig}”)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                });
            });
        } else if (currentQuery.category === 'figures') {
            // Comprehensive figures rubric with nested conditionals
            figuresInfo.forEach((fig: string, idx: number) => {
                const baseId = `figures_fig_${idx + 1}`;
                
                // Instruction
                questions.push({
                    id: `${baseId}_instruction`,
                    questionText: `Note: 1 figure is considered anything labeled with an individual number (Figure 1, Figure 2, etc.). If a figure has multiple parts (Figure 1a, Figure 1b, etc.), then the single figure is the aggregation of all of those parts, so you'll evaluate the criteria for ${fig} (inclusive of all parts), not for each sub-part.`,
                    questionType: 'instruction',
                });
                
                // Q1: Accuracy
                questions.push({
                    id: `${baseId}_q1_accuracy`,
                    questionText: `1. Are all described elements for ${fig} factually correct?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                });
                
                // Q2: Multi-part Coverage (with nested conditionals)
                questions.push({
                    id: `${baseId}_q2_multipart`,
                    questionText: `2. Is ${fig} multi-part (e.g., 1a, 1b, …, 1f)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q2a_subpart_referenced`,
                                    questionText: `2A. Is at least one sub-part of ${fig} (expicitly or implicitly) referenced in the response (e.g., 1a, or 1d)?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    conditionalFollowUps: [
                                        {
                                            triggerAnswer: 'Yes',
                                            followUpQuestions: [
                                                {
                                                    id: `${baseId}_q2a_i_all_subparts`,
                                                    questionText: `2A.i. Are all other sub-parts of ${fig} mentioned or described (even if briefly)?`,
                                                    questionType: 'multiple-choice',
                                                    options: ['Yes', 'No'],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                });
                
                // Q3: Visual Order
                questions.push({
                    id: `${baseId}_q3_visual_order`,
                    questionText: `3. Is there a natural reading or visual order in ${fig} (e.g., top to bottom, left to right, labeled sequence of steps)?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q3a_follows_order`,
                                    questionText: `3A. Does the response describe ${fig} in that natural visual order?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                            ],
                        },
                    ],
                });
                
                // Q4: Graph or Trend (complex nested structure)
                questions.push({
                    id: `${baseId}_q4_graph_trend`,
                    questionText: `4. Does ${fig} contain a graph, chart, or trend?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q4a_axis`,
                                    questionText: `4A. Is there an axis, or some other measurement/metrics associated with ${fig}?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    conditionalFollowUps: [
                                        {
                                            triggerAnswer: 'Yes',
                                            followUpQuestions: [
                                                {
                                                    id: `${baseId}_q4a_i_bounds`,
                                                    questionText: `4A.i. Does the model response specify the bounds of the axis / measurement units for ${fig}?`,
                                                    questionType: 'multiple-choice',
                                                    options: ['Yes', 'No'],
                                                },
                                            ],
                                        },
                                    ],
                                },
                                {
                                    id: `${baseId}_q4b_trend_direction`,
                                    questionText: `4B. Does the response describe the direction / shape of the trend in ${fig}?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                },
                                {
                                    id: `${baseId}_q4c_missing_elements`,
                                    questionText: `4C. Does the response have any missing elements necessary for interpretation of the trend in ${fig} if unable to visually access the figure?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    additionalNotes: 'Please specify what elements are missing in the notes field below.',
                                },
                            ],
                        },
                    ],
                });
                
                // Q5: Coverage of Important Visual Components
                questions.push({
                    id: `${baseId}_q5_identify_components`,
                    questionText: `5. Identify the important components of ${fig} necessary for scientific interpretation. Please list each component and rationalize why it is necessary.`,
                    questionType: 'long-text',
                });
                questions.push({
                    id: `${baseId}_q5a_components_described`,
                    questionText: `5A. For each essential component identified above, is the component described in the response?`,
                    questionType: 'multiple-choice',
                    options: ['None', 'Some', 'All'],
                });
                
                // Q6: Visual Semantics
                questions.push({
                    id: `${baseId}_q6_visual_semantics`,
                    questionText: `6. Are visual semantics such as color, shapes, sizes, etc. important for interpreting ${fig}?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    additionalNotes: 'If yes, please specify which visual semantics and why in the notes field below.',
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'Yes',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q6a_semantics_described`,
                                    questionText: `6A. Are all relevant visual semantics described in the response?`,
                                    questionType: 'multiple-choice',
                                    options: ['None', 'Some', 'All'],
                                },
                            ],
                        },
                    ],
                });
                
                // Q7: Reducing Visual Verbosity
                questions.push({
                    id: `${baseId}_q7_visual_detail_requested`,
                    questionText: `7. Was visual detail (visual semantics, specific components) explicitly requested in the user query?`,
                    questionType: 'multiple-choice',
                    options: ['Yes', 'No'],
                    conditionalFollowUps: [
                        {
                            triggerAnswer: 'No',
                            followUpQuestions: [
                                {
                                    id: `${baseId}_q7a_includes_visual_details`,
                                    questionText: `7A. Does the response include visual details about ${fig}?`,
                                    questionType: 'multiple-choice',
                                    options: ['Yes', 'No'],
                                    conditionalFollowUps: [
                                        {
                                            triggerAnswer: 'Yes',
                                            followUpQuestions: [
                                                {
                                                    id: `${baseId}_q7a_i_necessary_details`,
                                                    questionText: `7A.i. Are the included visual details all necessary to interpret the key results in ${fig}? (This is an atomic answer – if even one visual detail that was included is not necessary, mark No)`,
                                                    questionType: 'multiple-choice',
                                                    options: ['Yes', 'No'],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                });
            });
        }
        
        // Add conditional follow-ups based on current responses
        const allQuestions: RubricQuestion[] = [];
        const processQuestion = (question: RubricQuestion) => {
            allQuestions.push(question);
            
            // Check if this question has conditional follow-ups
            if (question.conditionalFollowUps) {
                const currentAnswer = responses[question.id];
                const matchingFollowUp = question.conditionalFollowUps.find(
                    (followUp) => followUp.triggerAnswer === currentAnswer
                );
                
                if (matchingFollowUp) {
                    matchingFollowUp.followUpQuestions.forEach(processQuestion);
                }
            }
        };
        
        questions.forEach(processQuestion);
        return allQuestions;
    }, [batch, currentIndex, responses]);

    // Get questions grouped by figure (for figures category with tabs)
    const getRubricQuestionsByFigure = useCallback((): { figureName: string; questions: RubricQuestion[] }[] => {
        if (!batch) return [];
        
        const currentQuery = batch.queries[currentIndex];
        if (!currentQuery || currentQuery.category !== 'figures') return [];
        
        const figuresInfo = (currentQuery as any).figures_info || [];
        const groupedQuestions: { figureName: string; questions: RubricQuestion[] }[] = [];
        
        figuresInfo.forEach((fig: string, idx: number) => {
            const baseId = `figures_fig_${idx + 1}`;
            const figQuestions: RubricQuestion[] = [];
            
            // Generate all questions for this specific figure
            // Instruction
            figQuestions.push({
                id: `${baseId}_instruction`,
                questionText: `Note: 1 figure is considered anything labeled with an individual number (Figure 1, Figure 2, etc.). If a figure has multiple parts (Figure 1a, Figure 1b, etc.), then the single figure is the aggregation of all of those parts, so you'll evaluate the criteria for this figure (inclusive of all parts), not for each sub-part.`,
                questionType: 'instruction',
            });
            
            // Q1-Q7 (same structure as before but without "(For ${fig})" prefix)
            figQuestions.push({
                id: `${baseId}_q1_accuracy`,
                questionText: `1. Are all described elements for ${fig} factually correct?`,
                questionType: 'multiple-choice',
                options: ['Yes', 'No'],
            });
            
            figQuestions.push({
                id: `${baseId}_q2_multipart`,
                questionText: `2. Is ${fig} multi-part (e.g., 1a, 1b, …, 1f)?`,
                questionType: 'multiple-choice',
                options: ['Yes', 'No'],
                conditionalFollowUps: [
                    {
                        triggerAnswer: 'Yes',
                        followUpQuestions: [
                            {
                                id: `${baseId}_q2a_subpart_referenced`,
                                questionText: `2A. Is at least one sub-part of ${fig} (expicitly or implicitly) referenced in the response (e.g., 1a, or 1d)?`,
                                questionType: 'multiple-choice',
                                options: ['Yes', 'No'],
                                conditionalFollowUps: [
                                    {
                                        triggerAnswer: 'Yes',
                                        followUpQuestions: [
                                            {
                                                id: `${baseId}_q2a_i_all_subparts`,
                                                questionText: `2A.i. Are all other sub-parts of ${fig} mentioned or described (even if briefly)?`,
                                                questionType: 'multiple-choice',
                                                options: ['Yes', 'No'],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            });
            
            figQuestions.push({
                id: `${baseId}_q3_visual_order`,
                questionText: `3. Is there a natural reading or visual order in ${fig} (e.g., top to bottom, left to right, labeled sequence of steps)?`,
                questionType: 'multiple-choice',
                options: ['Yes', 'No'],
                conditionalFollowUps: [
                    {
                        triggerAnswer: 'Yes',
                        followUpQuestions: [
                            {
                                id: `${baseId}_q3a_follows_order`,
                                questionText: `3A. Does the response describe ${fig} in that natural visual order?`,
                                questionType: 'multiple-choice',
                                options: ['Yes', 'No'],
                            },
                        ],
                    },
                ],
            });
            
            figQuestions.push({
                id: `${baseId}_q4_graph_trend`,
                questionText: `4. Does ${fig} contain a graph, chart, or trend?`,
                questionType: 'multiple-choice',
                options: ['Yes', 'No'],
                conditionalFollowUps: [
                    {
                        triggerAnswer: 'Yes',
                        followUpQuestions: [
                            {
                                id: `${baseId}_q4a_axis`,
                                questionText: `4A. Is there an axis, or some other measurement/metrics associated with ${fig}?`,
                                questionType: 'multiple-choice',
                                options: ['Yes', 'No'],
                                conditionalFollowUps: [
                                    {
                                        triggerAnswer: 'Yes',
                                        followUpQuestions: [
                                            {
                                                id: `${baseId}_q4a_i_bounds`,
                                                questionText: `4A.i. Does the model response specify the bounds of the axis / measurement units for ${fig}?`,
                                                questionType: 'multiple-choice',
                                                options: ['Yes', 'No'],
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                id: `${baseId}_q4b_trend_direction`,
                                questionText: `4B. Does the response describe the direction / shape of the trend in ${fig}?`,
                                questionType: 'multiple-choice',
                                options: ['Yes', 'No'],
                            },
                            {
                                id: `${baseId}_q4c_missing_elements`,
                                questionText: `4C. Does the response have any missing elements necessary for interpretation of the trend in ${fig} if unable to visually access the figure?`,
                                questionType: 'multiple-choice',
                                options: ['Yes', 'No'],
                                additionalNotes: 'Please specify what elements are missing in the notes field below.',
                            },
                        ],
                    },
                ],
            });
            
            figQuestions.push({
                id: `${baseId}_q5_identify_components`,
                questionText: `5. Identify the important components of ${fig} necessary for scientific interpretation. Please list each component and rationalize why it is necessary.`,
                questionType: 'long-text',
            });
            figQuestions.push({
                id: `${baseId}_q5a_components_described`,
                questionText: `5A. For each essential component identified above, is the component described in the response?`,
                questionType: 'multiple-choice',
                options: ['None', 'Some', 'All'],
            });
            
            figQuestions.push({
                id: `${baseId}_q6_visual_semantics`,
                questionText: `6. Are visual semantics such as color, shapes, sizes, etc. important for interpreting ${fig}?`,
                questionType: 'multiple-choice',
                options: ['Yes', 'No'],
                additionalNotes: 'If yes, please specify which visual semantics and why in the notes field below.',
                conditionalFollowUps: [
                    {
                        triggerAnswer: 'Yes',
                        followUpQuestions: [
                            {
                                id: `${baseId}_q6a_semantics_described`,
                                questionText: `6A. Are all relevant visual semantics described in the response?`,
                                questionType: 'multiple-choice',
                                options: ['None', 'Some', 'All'],
                            },
                        ],
                    },
                ],
            });
            
            figQuestions.push({
                id: `${baseId}_q7_visual_detail_requested`,
                questionText: `7. Was visual detail (visual semantics, specific components) explicitly requested in the user query?`,
                questionType: 'multiple-choice',
                options: ['Yes', 'No'],
                conditionalFollowUps: [
                    {
                        triggerAnswer: 'No',
                        followUpQuestions: [
                            {
                                id: `${baseId}_q7a_includes_visual_details`,
                                questionText: `7A. Does the response include visual details about ${fig}?`,
                                questionType: 'multiple-choice',
                                options: ['Yes', 'No'],
                                conditionalFollowUps: [
                                    {
                                        triggerAnswer: 'Yes',
                                        followUpQuestions: [
                                            {
                                                id: `${baseId}_q7a_i_necessary_details`,
                                                questionText: `7A.i. Are the included visual details all necessary to interpret the key results in ${fig}? (This is an atomic answer – if even one visual detail that was included is not necessary, mark No)`,
                                                questionType: 'multiple-choice',
                                                options: ['Yes', 'No'],
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                ],
            });
            
            // Process conditionals
            const processedQuestions: RubricQuestion[] = [];
            const processQuestion = (question: RubricQuestion) => {
                processedQuestions.push(question);
                
                if (question.conditionalFollowUps) {
                    const currentAnswer = responses[question.id];
                    const matchingFollowUp = question.conditionalFollowUps.find(
                        (followUp) => followUp.triggerAnswer === currentAnswer
                    );
                    
                    if (matchingFollowUp) {
                        matchingFollowUp.followUpQuestions.forEach(processQuestion);
                    }
                }
            };
            
            figQuestions.forEach(processQuestion);
            
            groupedQuestions.push({
                figureName: fig,
                questions: processedQuestions
            });
        });
        
        return groupedQuestions;
    }, [batch, currentIndex, responses]);

    // Handle response change
    const handleResponseChange = (questionId: string, answer: string | string[]) => {
        setResponses((prev: Record<string, string | string[]>) => ({
            ...prev,
            [questionId]: answer,
        }));
    };

    // Navigation handlers
    const handlePrevious = () => {
        if (currentIndex > 0) {
            saveCurrentToMemory();
            const newIndex = currentIndex - 1;
            setCurrentIndex(newIndex);
            if (batch) loadResponsesFromMemory(batch.queries[newIndex].id);
        }
    };

    const handleNext = () => {
        if (batch && currentIndex < batch.queries.length - 1) {
            saveCurrentToMemory();
            const newIndex = currentIndex + 1;
            setCurrentIndex(newIndex);
            loadResponsesFromMemory(batch.queries[newIndex].id);
        }
    };

    // Submit handler - downloads all annotations as JSON
    const handleSubmit = () => {
        if (!batch) return;

        // Save current query to memory first
        saveCurrentToMemory();

        // Get all in-memory annotations including the one we just saved
        const allInMemoryAnnotations = {
            ...inMemoryAnnotations,
            [batch.queries[currentIndex].id]: {
                queryId: batch.queries[currentIndex].id,
                responses: Object.entries(responses).map(([questionId, answer]) => ({
                    questionId,
                    answer: answer as string | string[],
                })),
                additionalNotes,
                timestamp: new Date().toISOString(),
                flagged: isFlagged,
                flagRationale: isFlagged ? flagRationale : undefined,
            }
        };

        // Validate that all queries have required base questions answered
        const incompleteQueries: string[] = [];
        batch.queries.forEach((query: Query, queryIndex: number) => {
            const annotation = allInMemoryAnnotations[query.id];
            if (!annotation) {
                incompleteQueries.push(`Query ${queryIndex + 1}: No responses provided`);
                return;
            }

            // Get the base questions for this query based on category
            const figuresInfo = (query as any).figures_info || [];
            const requiredQuestions: string[] = [];
            const responsesMap = new Map(annotation.responses.map((r: {questionId: string, answer: string | string[]}) => [r.questionId, r.answer]));

            if (query.category === 'general') {
                // For general: general_fig_1, general_fig_2, etc.
                figuresInfo.forEach((_: string, idx: number) => {
                    const baseQuestionId = `general_fig_${idx + 1}`;
                    requiredQuestions.push(baseQuestionId);
                    requiredQuestions.push(`general_fig_${idx + 1}_q2_represents`);

                    // Follow-ups are required only when the base answer is "No"
                    const baseAnswer = responsesMap.get(baseQuestionId);
                    if (baseAnswer === 'No') {
                        requiredQuestions.push(`general_fig_${idx + 1}_q1a_appropriate`);
                        requiredQuestions.push(`general_fig_${idx + 1}_q1b_explicitly_out_of_scope`);
                    }
                });
            } else if (query.category === 'sections') {
                // For sections: sections_fig_1, sections_fig_2, etc.
                figuresInfo.forEach((_: string, idx: number) => {
                    const baseQuestionId = `sections_fig_${idx + 1}`;
                    requiredQuestions.push(baseQuestionId);
                    requiredQuestions.push(`sections_fig_${idx + 1}_q2_represents`);

                    // Follow-ups are required only when the base answer is "No"
                    const baseAnswer = responsesMap.get(baseQuestionId);
                    if (baseAnswer === 'No') {
                        requiredQuestions.push(`sections_fig_${idx + 1}_q1a_appropriate`);
                        requiredQuestions.push(`sections_fig_${idx + 1}_q1b_explicitly_out_of_scope`);
                    }
                });
            } else if (query.category === 'figures') {
                // For figures: q1-q7 for each figure (but not sub-questions)
                figuresInfo.forEach((_: string, idx: number) => {
                    const baseId = `figures_fig_${idx + 1}`;
                    requiredQuestions.push(`${baseId}_q1_accuracy`);
                    requiredQuestions.push(`${baseId}_q2_multipart`);
                    requiredQuestions.push(`${baseId}_q3_visual_order`);
                    requiredQuestions.push(`${baseId}_q4_graph_trend`);
                    // q5 is long-text, optional
                    requiredQuestions.push(`${baseId}_q5a_components_described`);
                    requiredQuestions.push(`${baseId}_q6_visual_semantics`);
                    requiredQuestions.push(`${baseId}_q7_visual_detail_requested`);
                });
            }

            // Check if all required questions have answers
            const missingQuestions = requiredQuestions.filter(qId => {
                const answer = responsesMap.get(qId);
                return !answer || (Array.isArray(answer) && answer.length === 0) || answer === '';
            });

            if (missingQuestions.length > 0) {
                incompleteQueries.push(`Query ${queryIndex + 1}: Missing ${missingQuestions.length} required answer(s)`);
            }
        });

        if (incompleteQueries.length > 0) {
            setUnansweredQueries(incompleteQueries);
            setValidationDialogOpen(true);
            return;
        }

        // All queries annotated - proceed with download
        downloadAnnotations(allInMemoryAnnotations);
    };

    const downloadAnnotations = (allInMemoryAnnotations: Record<string, Annotation>) => {
        if (!batch) return;

        // Collect all annotations for the batch
        const allAnnotations = Object.values(allInMemoryAnnotations).map((annotation: Annotation) => {
            const query = batch.queries.find((q: Query) => q.id === annotation.queryId);
            if (!query) return null;

            const submitted: Record<string, any> = {
                qa_id: annotation.queryId,
                category: query.category,
            };

            // All categories now group by figure/table
            const figureResponses: Record<string, Record<string, string | string[]>> = {};

            annotation.responses.forEach((resp: AnnotationResponse) => {
                // Skip instruction "questions"
                if (resp.questionId.includes('_instruction')) {
                    return;
                }
                
                // Parse question ID patterns based on category:
                // general: general_fig_1 -> index 0 in figures_info -> "Table 1" or "Figure 4"
                // sections: sections_fig_2 -> index 1 in figures_info
                // figures: figures_fig_1_q2a_i_all_subparts -> index 0 in figures_info
                
                let figureIndex: number | undefined;
                let questionId: string | undefined;
                
                if (query.category === 'general') {
                    // general_fig_1 -> index 0, q1
                    // general_fig_1_q1a_appropriate -> index 0, q1a
                    // general_fig_1_q1b_explicitly_out_of_scope -> index 0, q1b
                    // general_fig_1_q2_represents -> index 0, q2
                    const match = resp.questionId.match(/^general_fig_(\d+)(?:_(q1a_appropriate|q1b_explicitly_out_of_scope|q2_represents))?$/);
                    const figureIndexStr = match?.[1];
                    if (figureIndexStr) {
                        figureIndex = parseInt(figureIndexStr, 10) - 1; // Convert to 0-indexed
                        const suffix = match?.[2];
                        if (suffix === 'q1a_appropriate') {
                            questionId = 'q1a';
                        } else if (suffix === 'q1b_explicitly_out_of_scope') {
                            questionId = 'q1b';
                        } else if (suffix === 'q2_represents') {
                            questionId = 'q2';
                        } else {
                            questionId = 'q1';
                        }
                    }
                } else if (query.category === 'sections') {
                    // sections_fig_2 -> index 1, q1
                    // sections_fig_2_q1a_appropriate -> index 1, q1a
                    // sections_fig_2_q1b_explicitly_out_of_scope -> index 1, q1b
                    // sections_fig_2_q2_represents -> index 1, q2
                    const match = resp.questionId.match(/^sections_fig_(\d+)(?:_(q1a_appropriate|q1b_explicitly_out_of_scope|q2_represents))?$/);
                    const figureIndexStr = match?.[1];
                    if (figureIndexStr) {
                        figureIndex = parseInt(figureIndexStr, 10) - 1; // Convert to 0-indexed
                        const suffix = match?.[2];
                        if (suffix === 'q1a_appropriate') {
                            questionId = 'q1a';
                        } else if (suffix === 'q1b_explicitly_out_of_scope') {
                            questionId = 'q1b';
                        } else if (suffix === 'q2_represents') {
                            questionId = 'q2';
                        } else {
                            questionId = 'q1';
                        }
                    }
                } else if (query.category === 'figures') {
                    // figures_fig_1_q2a_i_all_subparts -> index 0
                    const parts = resp.questionId.split('_');
                    // parts example: ['figures', 'fig', '1', 'q2a', 'i', 'all', 'subparts']
                    
                    if (parts.length >= 4 && parts[0] === 'figures' && parts[1] === 'fig') {
                        const figureIndexStr = parts[2];
                        const baseQuestionId = parts[3];
                        if (!figureIndexStr || !baseQuestionId) {
                            return;
                        }

                        figureIndex = parseInt(figureIndexStr, 10) - 1; // Convert to 0-indexed
                        questionId = baseQuestionId; // e.g., 'q2a' or 'q1'
                        
                        // Check if the next part is a roman numeral (i, ii, iii, iv, v, etc.)
                        if (parts[4] && /^[ivxlcdm]+$/i.test(parts[4])) {
                            questionId += parts[4]; // e.g., 'q2a' + 'i' = 'q2ai'
                        }
                    }
                }
                
                if (figureIndex !== undefined && questionId) {
                    // Get the actual figure/table name from figures_info
                    const figuresInfo = (query as any).figures_info || [];
                    const figureLabel = figuresInfo[figureIndex]; // e.g., "Table 1" or "Figure 4"
                    
                    if (figureLabel) {
                        // Parse "Table 1" -> "table1", "Figure 4" -> "figure4"
                        const labelMatch = figureLabel.match(/^(Figure|Table)\s+(\d+)/i);
                        let figureName: string;
                        
                        if (labelMatch) {
                            const type = labelMatch[1].toLowerCase(); // "figure" or "table"
                            const num = labelMatch[2]; // "1", "4", "5", etc.
                            figureName = `${type}${num}`; // "figure1", "table1", etc.
                        } else {
                            // Fallback: use generic figure{index+1}
                            figureName = `figure${figureIndex + 1}`;
                        }
                        
                        if (!figureResponses[figureName]) {
                            figureResponses[figureName] = {};
                        }
                        
                        figureResponses[figureName][questionId] = resp.answer;
                    }
                } else {
                    // Fallback for any unexpected format
                    console.warn('Unexpected question ID format:', resp.questionId);
                }
            });

            // Add each figure's responses to the submitted object (sorted by type then number)
            Object.keys(figureResponses).sort((a, b) => {
                // Extract type and number: "figure1" -> ["figure", "1"], "table5" -> ["table", "5"]
                const matchA = a.match(/^(figure|table)(\d+)$/i);
                const matchB = b.match(/^(figure|table)(\d+)$/i);
                
                if (matchA && matchB) {
                    // Sort by type first (figure before table), then by number
                    if (matchA[1] !== matchB[1]) {
                        return matchA[1].localeCompare(matchB[1]);
                    }
                    return parseInt(matchA[2]) - parseInt(matchB[2]);
                }
                return a.localeCompare(b);
            }).forEach(figureName => {
                submitted[figureName] = figureResponses[figureName];
            });

            // Add additional notes if present
            if (annotation.additionalNotes) {
                submitted['additional_notes'] = annotation.additionalNotes;
            }

            // Add flag data if present
            if (annotation.flagged) {
                submitted['flagged'] = true;
                submitted['flag_rationale'] = annotation.flagRationale || '';
            }

            return submitted;
        }).filter(Boolean); // Remove nulls

        // Create downloadable JSON
        const dataStr = JSON.stringify(allAnnotations, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        
        // Trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `annotations_${batchId}_${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };


    // Render loading state
    if (batchLoading || !batch) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h5" gutterBottom>
                    Loading annotation batch...
                </Typography>
                <LinearProgress />
            </Box>
        );
    }

    // Render error state
    if (batchError) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error">
                    Failed to load batch: {batchError.message}
                </Alert>
            </Box>
        );
    }

    const currentQuery = batch.queries[currentIndex];
    const rubricQuestions = getRubricQuestions();
    const progress = ((currentIndex + 1) / batch.queries.length) * 100;

    return (
        <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Progress Bar and Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2">
                        Query {currentIndex + 1} of {batch.queries.length}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <MenuBook fontSize="small" color="primary" />
                        <Link 
                            href="/tutorial"
                            underline="hover"
                            sx={{ fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                window.open('/tutorial', '_blank');
                            }}>
                            Tutorial Examples
                        </Link>
                        <Typography variant="body2" color="text.secondary">|</Typography>
                        <HomeIcon fontSize="small" color="primary" />
                        <Link 
                            href="/"
                            underline="hover"
                            sx={{ fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
                            onClick={(e) => {
                                e.preventDefault();
                                window.location.href = '/';
                            }}>
                            Back to Home
                        </Link>
                    </Stack>
                </Stack>
                <LinearProgress variant="determinate" value={progress} />
            </Box>

            {/* Main Content */}
            <Grid container sx={{ flex: 1, width: '100%', minHeight: 0 }}>
                {/* Left Panel - Query, Response, Figures */}
                <Grid 
                    item 
                    xs={6}
                    sx={{ 
                        overflow: 'auto', 
                        p: 3,
                        borderRight: 1,
                        borderColor: 'divider',
                        height: '100%',
                        width: '50%',
                        flexBasis: '50%'
                    }}
                >
                    <Stack spacing={3}>
                        {/* Paper Information - Prominent at top */}
                        {(currentQuery.paper_title || currentQuery.pdf) && (
                            <Paper elevation={2} sx={{ p: 3, bgcolor: '#f5f5f5', border: 2, borderColor: 'primary.main' }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                    <Box sx={{ flex: 1 }}>
                                        {currentQuery.paper_title && (
                                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                                {currentQuery.paper_title}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary">
                                            Source Paper
                                        </Typography>
                                    </Box>
                                    {currentQuery.pdf && (
                                        <Button
                                            variant="contained"
                                            href={currentQuery.pdf}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            size="small"
                                        >
                                            View PDF
                                        </Button>
                                    )}
                                </Stack>
                            </Paper>
                        )}

                        {/* Query */}
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Query
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Category: {currentQuery.category}
                            </Typography>
                            <Typography variant="body1">{currentQuery.user_query}</Typography>
                        </Paper>

                        {/* Response */}
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                                <Typography variant="h6" color="primary">
                                    Response
                                </Typography>
                                <Button
                                    variant={isFlagged ? "contained" : "outlined"}
                                    color={isFlagged ? "warning" : "default"}
                                    size="small"
                                    startIcon={isFlagged ? <Flag /> : <FlagOutlined />}
                                    onClick={() => setFlagDialogOpen(true)}
                                    sx={{ minWidth: 120 }}
                                >
                                    {isFlagged ? "Flagged" : "Flag"}
                                </Button>
                            </Stack>
                            {isFlagged && flagRationale && (
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        display: 'block',
                                        mb: 2,
                                        p: 1.5,
                                        bgcolor: 'warning.light',
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: 'warning.main'
                                    }}
                                >
                                    <strong>Flag reason:</strong> {flagRationale}
                                </Typography>
                            )}
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                {currentQuery.model_response}
                            </Typography>
                        </Paper>

                        {/* Associated Figures */}
                        {currentQuery.figs && currentQuery.figs.length > 0 && (
                            <Paper elevation={2} sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    Associated Figures
                                </Typography>
                                <Stack spacing={2}>
                                    {currentQuery.figs.map((figUrl, idx) => (
                                        <Box key={idx}>
                                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                                {(currentQuery as any).figures_info?.[idx] || `Figure ${idx + 1}`}
                                            </Typography>
                                            <Box
                                                component="img"
                                                src={figUrl}
                                                alt={`Figure ${idx + 1}`}
                                                sx={{
                                                    maxWidth: '100%',
                                                    height: 'auto',
                                                    border: 1,
                                                    borderColor: 'grey.300',
                                                    borderRadius: 1,
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Grid>

                {/* Right Panel - Rubric Questions */}
                <Grid
                    item
                    xs={6}
                    sx={{
                        overflow: 'auto',
                        p: 3,
                        bgcolor: 'grey.50',
                        height: '100%',
                        width: '50%',
                        flexBasis: '50%'
                    }}
                >
                    <Stack spacing={3}>
                        <Typography variant="h5" gutterBottom>
                            Rubric Questions
                        </Typography>

                        {/* Category-specific Task Instructions */}
                        {currentQuery.category === 'general' && (
                            <Paper elevation={2} sx={{ p: 3, bgcolor: '#E3F2FD', border: 1, borderColor: '#90CAF9' }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    📋 Task Instructions for General Queries
                                </Typography>
                                <Typography variant="body2">
                                    For these queries, you should skim at least the abstract, introduction, and results sections of the paper to effectively evaluate the response and figures. Complete the multiple choice questions, and use the notes field to capture any additional thoughts or observations, including your rationale for arriving at the answer you did.
                                </Typography>
                            </Paper>
                        )}

                        {currentQuery.category === 'sections' && (
                            <Paper elevation={2} sx={{ p: 3, bgcolor: '#E3F2FD', border: 1, borderColor: '#90CAF9' }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    📋 Task Instructions for Sections Queries
                                </Typography>
                                <Typography variant="body2">
                                    For these queries, the figures and tables are provided to you, but you also have access to the full paper to check anything for complete evaluation of the response and figures. Complete the multiple choice questions, and use the notes field to capture any additional thoughts or observations, including your rationale for arriving at the answer you did.
                                </Typography>
                            </Paper>
                        )}

                        {currentQuery.category === 'figures' && (
                            <Paper elevation={2} sx={{ p: 3, bgcolor: '#E3F2FD', border: 1, borderColor: '#90CAF9' }}>
                                <Typography variant="h6" gutterBottom color="primary">
                                    📋 Task Instructions for Figures Queries
                                </Typography>
                                <Typography variant="body2">
                                    For these queries, you should open the paper and review the relevant section, methodology, or specific evaluation that is being asked about prior to evaluating the response and figures. Complete the multiple choice questions, and use the notes field to capture any additional thoughts or observations, including your rationale for arriving at the answer you did.
                                </Typography>
                            </Paper>
                        )}

                        {/* Debug: Show if rubric is empty */}
                        {rubricQuestions.length === 0 && currentQuery.category !== 'figures' && (
                            <Alert severity="info">
                                No rubric questions generated. Category: {currentQuery.category}, 
                                Figures: {(currentQuery as any).figures_info?.join(', ') || 'none'}
                            </Alert>
                        )}

                        {/* Render tabs for figures category with multiple figures */}
                        {currentQuery.category === 'figures' && (() => {
                            const figureGroups = getRubricQuestionsByFigure();
                            
                            if (figureGroups.length === 0) {
                                return (
                                    <Alert severity="info">
                                        No figures found for this query.
                                    </Alert>
                                );
                            }
                            
                            if (figureGroups.length === 1) {
                                // Single figure - no tabs needed
                                return (
                                    <>
                                        <Typography variant="h6" gutterBottom color="primary">
                                            {figureGroups[0].figureName}
                                        </Typography>
                                        {figureGroups[0].questions.map((question) => (
                                            <RubricQuestionComponent
                                                key={question.id}
                                                question={question}
                                                value={responses[question.id]}
                                                onChange={(answer) => handleResponseChange(question.id, answer)}
                                            />
                                        ))}
                                    </>
                                );
                            }
                            
                            // Multiple figures - use tabs
                            return (
                                <Box>
                                    <Tabs 
                                        value={currentFigureTab} 
                                        onChange={(e, newValue) => setCurrentFigureTab(newValue)}
                                        variant="scrollable"
                                        scrollButtons="auto"
                                    >
                                        {figureGroups.map((group, idx) => (
                                            <Tab key={idx} label={group.figureName} />
                                        ))}
                                    </Tabs>
                                    
                                    {figureGroups.map((group, idx) => (
                                        <Box 
                                            key={idx} 
                                            role="tabpanel" 
                                            hidden={currentFigureTab !== idx}
                                            sx={{ pt: 3 }}
                                        >
                                            {currentFigureTab === idx && (
                                                <Stack spacing={3}>
                                                    {group.questions.map((question) => (
                                                        <RubricQuestionComponent
                                                            key={question.id}
                                                            question={question}
                                                            value={responses[question.id]}
                                                            onChange={(answer) => handleResponseChange(question.id, answer)}
                                                        />
                                                    ))}
                                                </Stack>
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            );
                        })()}

                        {/* Render regular questions for non-figures categories */}
                        {currentQuery.category !== 'figures' && rubricQuestions.map((question) => (
                            <RubricQuestionComponent
                                key={question.id}
                                question={question}
                                value={responses[question.id]}
                                onChange={(answer) => handleResponseChange(question.id, answer)}
                            />
                        ))}

                        {/* Additional Notes */}
                        <Paper elevation={2} sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Additional Notes
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Submit any thoughts not captured by the rubric
                            </Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={6}
                                value={additionalNotes}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAdditionalNotes(e.target.value)}
                                placeholder="Enter any additional observations or comments..."
                            />
                        </Paper>
                    </Stack>
                </Grid>
            </Grid>

            {/* Navigation Bar */}
            <Box
                sx={{
                    p: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexShrink: 0,
                }}
            >
                <Button
                    startIcon={<ArrowBack />}
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                    variant="outlined"
                >
                    Previous
                </Button>

                <Typography variant="body2" color="text.secondary">
                    Query {currentIndex + 1} of {batch.queries.length}
                </Typography>

                {currentIndex === batch.queries.length - 1 ? (
                    // Show Submit button only on the last query
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="success"
                        sx={{ fontWeight: 'bold' }}
                        title="Submit all annotations (requires all queries to be completed)"
                    >
                        Submit All Annotations
                    </Button>
                ) : (
                    // Show Next button on all other queries
                    <Button
                        endIcon={<ArrowForward />}
                        onClick={handleNext}
                        variant="outlined"
                    >
                        Next
                    </Button>
                )}
            </Box>

            {/* Validation Dialog */}
            <Dialog
                open={validationDialogOpen}
                onClose={() => setValidationDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Incomplete Annotations</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You must answer all required multiple-choice questions before submitting. The following queries have incomplete answers:
                    </DialogContentText>
                    <Box component="ul" sx={{ mt: 2 }}>
                        {unansweredQueries.map((query, idx) => (
                            <Typography component="li" key={idx} sx={{ mb: 1 }}>
                                {query}
                            </Typography>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Flag Dialog */}
            <Dialog
                open={flagDialogOpen}
                onClose={() => setFlagDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Flag color={isFlagged ? "warning" : "action"} />
                        <span>Flag Query/Response Pair</span>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Use this flag to indicate if the response is completely unrelated to the query or contains hallucinated information. 
                        This is optional and does not affect your ability to submit annotations.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        label="Reason for flagging (required if flagging)"
                        placeholder="Describe why this response is problematic..."
                        value={flagRationale}
                        onChange={(e) => setFlagRationale(e.target.value)}
                        variant="outlined"
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setFlagDialogOpen(false);
                        // Reset to previous state if closing without saving
                    }}>
                        Cancel
                    </Button>
                    {isFlagged && (
                        <Button 
                            onClick={() => {
                                setIsFlagged(false);
                                setFlagRationale('');
                                setFlagDialogOpen(false);
                            }}
                            color="inherit"
                        >
                            Remove Flag
                        </Button>
                    )}
                    <Button 
                        onClick={() => {
                            if (flagRationale.trim()) {
                                setIsFlagged(true);
                                setFlagDialogOpen(false);
                            }
                        }}
                        color="warning"
                        variant="contained"
                        disabled={!flagRationale.trim()}
                    >
                        {isFlagged ? 'Update Flag' : 'Add Flag'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Component to render individual rubric questions
interface RubricQuestionComponentProps {
    question: RubricQuestion;
    value: string | string[] | undefined;
    onChange: (answer: string | string[]) => void;
}

const RubricQuestionComponent = ({
    question,
    value,
    onChange,
}: RubricQuestionComponentProps) => {
    // Handle instruction type (display only, no input)
    if (question.questionType === 'instruction') {
        return (
            <Paper elevation={1} sx={{ p: 2, bgcolor: '#f5f5f5', borderLeft: '4px solid #1976d2' }}>
                <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                    {question.questionText}
                </Typography>
            </Paper>
        );
    }

    if (question.questionType === 'multiple-choice' && question.options) {
        return (
            <Paper elevation={2} sx={{ p: 3 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend">{question.questionText}</FormLabel>
                    {(question as any).additionalNotes && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                            {(question as any).additionalNotes}
                        </Typography>
                    )}
                    <RadioGroup
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                    >
                        {question.options.map((option) => (
                            <FormControlLabel
                                key={option}
                                value={option}
                                control={<Radio />}
                                label={option}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Paper>
        );
    }

    if (question.questionType === 'short-text') {
        return (
            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    {question.questionText}
                </Typography>
                <TextField
                    fullWidth
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Enter your answer..."
                />
            </Paper>
        );
    }

    if (question.questionType === 'long-text') {
        return (
            <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    {question.questionText}
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder="Enter your answer..."
                />
            </Paper>
        );
    }

    return null;
};
